import { toNum } from "./ViewerModal/utils/function";

export const addNA = (
  list: any
): { label: string; value: string }[] => {
  try {
    // Ensure we have a valid array
    const safeList = Array.isArray(list) ? list : [];

    // Normalize items: only objects with label & value, convert strings/numbers to { label, value }
    const normalizedList = safeList
      .filter(Boolean) // remove null/undefined/false
      .map((item) => {
        if (!item) return null;

        // If it's an object with label & value, use it
        if (
          typeof item === "object" &&
          "label" in item &&
          "value" in item
        ) {
          return {
            label: String(item.label ?? item.value ?? ""),
            value: String(item.value ?? item.label ?? ""),
          };
        }

        // If it's a string or number, convert to object
        if (typeof item === "string" || typeof item === "number") {
          const val = String(item);
          return { label: val, value: val };
        }

        // Ignore other types
        return null;
      })
      .filter(Boolean) as { label: string; value: string }[];

    // Check if NA already exists (case-insensitive)
    const hasNA = normalizedList.some(
      (item) => String(item.value).toLowerCase() === "na"
    );

    // Always append NA if not present
    return hasNA
      ? normalizedList
      : [...normalizedList, { label: "NA", value: "NA" }];
  } catch (err) {
    // Fallback in case of unexpected errors
    return [{ label: "NA", value: "NA" }];
  }
};


export const addWithoutNA = (
  list: any
): { label: string; value: string }[] => {
  try {
    // Ensure we have a valid array
    const safeList = Array.isArray(list) ? list : [];

    // Normalize items: only objects with label & value, convert strings/numbers to { label, value }
    const normalizedList = safeList
      .filter(Boolean) // remove null/undefined/false
      .map((item) => {
        if (!item) return null;

        // If it's an object with label & value, use it
        if (
          typeof item === "object" &&
          "label" in item &&
          "value" in item
        ) {
          return {
            label: String(item.label ?? item.value ?? ""),
            value: String(item.value ?? item.label ?? ""),
          };
        }

        // If it's a string or number, convert to object
        if (typeof item === "string" || typeof item === "number") {
          const val = String(item);
          return { label: val, value: val };
        }

        // Ignore other types
        return null;
      })
      .filter(Boolean) as { label: string; value: string }[];

    // Check if NA already exists (case-insensitive)
    const hasNA = normalizedList.some(
      (item) => String(item.value).toLowerCase() === "na"
    );

    // Always append NA if not present
    return hasNA
      ? normalizedList
      : [...normalizedList];
  } catch (err) {
    // Fallback in case of unexpected errors
    return [{ label: "NA", value: "NA" }];
  }
};


export const addNAObject = (
  list: any
): { label: string; value: string; taxCodes: any[] }[] => {
  try {
    if (!Array.isArray(list)) return [{ label: "NA", value: "NA", taxCodes: [] }];

    const normalized = list
      .filter(Boolean)
      .map((item) => ({
        label: String(item?.label ?? item?.value ?? ""),
        value: String(item?.value ?? item?.label ?? ""),
        taxCodes: Array.isArray(item?.taxCodes) ? item.taxCodes : [],
      }));

    const hasNA = normalized.some(
      (item) => item.value.toLowerCase() === "na"
    );

    return hasNA
      ? normalized
      : [...normalized, { label: "NA", value: "NA", taxCodes: [] }];
  } catch {
    return [{ label: "NA", value: "NA", taxCodes: [] }];
  }
};

export const addSummaryNA = (
  list: any
) => {
  try {
    // Normalize input to array
    const safeList = Array.isArray(list) ? list : [];

    const normalized = safeList
      .filter((item): item is NonNullable<typeof item> => item != null)
      .map((item) => {
        // Already a proper object with label & value → spread + ensure string types
        if (
          typeof item === "object" &&
          item !== null &&
          "label" in item &&
          "value" in item
        ) {
          return {
            ...item,                             // ← keeps rate, code, disabled, etc.
            label: String(item.label ?? ""),
            value: String(item.value ?? ""),
          };
        }

        // Plain string or number → convert to object
        if (typeof item === "string" || typeof item === "number") {
          const val = String(item);
          return { label: val, value: val };
        }

        // Unsupported type → skip
        return null;
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);

    // Check if "NA" already exists (case-insensitive on value)
    const hasNA = normalized.some(
      (item) => String(item.value).toLowerCase() === "na"
    );

    // Append NA only if missing
    if (hasNA) {
      return normalized;
    }

    return [
      ...normalized,
      { label: "NA", value: "NA" } as any, // type assertion to satisfy union
    ];
  } catch (err) {
    console.warn("addNA failed:", err);
    return [{ label: "NA", value: "NA" }];
  }
};

export const combineValues = (values: any, editedValues: any) => {
  console.log("edited values func", editedValues);
  let val = Array.isArray(values)
    ? values?.[values?.length - 1]?.values
    : values;

  const combinedObject: any = {};
  for (const key in val) {
    const originalValue = val[key];
    const editValue = editedValues[key];

    if (
      editValue !== undefined &&
      originalValue !== editValue &&
      editValue !== originalValue.originalValue
    ) {
      combinedObject[key] = {
        originalValue: originalValue.editFieldValue
          ? originalValue.editFieldValue
          : originalValue?.originalValue,
        editFieldValue: editValue,
      };
    } else {
      combinedObject[key] = {
        originalValue: originalValue.editFieldValue
          ? originalValue.editFieldValue
          : originalValue?.originalValue,
      };
    }
  }
  return combinedObject;
};


export const calculateDateRange = (range: any) => {
  const today = new Date();
  const startDate = new Date(today);
  if (range > 0) {
    startDate.setMonth(today.getMonth() - range);
  }
  const endDate = new Date();
  return { startDate, endDate };
};

export function canShowDocuments(workFlowData: any, accountDetails: any): boolean {
  if (
    Array.isArray(workFlowData?.canAdditionalDocument) &&
    workFlowData?.canAdditionalDocument?.length > 0 &&
    workFlowData.canAdditionalDocument.every(
      (item: any) => typeof item === "string" && item.includes("level-")
    )
  ) {
    const userRole = accountDetails?.role || "uploader";
    const userLevel = accountDetails?.level
      ? parseInt(accountDetails.level.split("-")[1])
      : 0;

    if (isNaN(userLevel)) {
      return false; // Invalid user level format
    }

    // Check if user's level exists in workflow data
    const levelExists = workFlowData.canAdditionalDocument.some(
      (level: string) => level === `level-${userLevel}`
    );

    // Check if user's role exists in workflow data
    const roleExists = workFlowData.canAdditionalDocument.includes(userRole);

    // Get maximum level from workflow data
    const levels = workFlowData.canAdditionalDocument
      .map((lvl: string) => {
        const parsed = parseInt(lvl.split("-")[1]);
        return isNaN(parsed) ? null : parsed;
      })
      .filter((lvl: any) => lvl !== null);

    const maxLevel = levels.length > 0 ? Math.max(...levels) : 0;

    // Check if user's level is at least the highest level
    const isAboveMaxLevel = userLevel >= maxLevel;

    return levelExists || roleExists || isAboveMaxLevel;
  }
  return false;
}

// export function sumLineAmounts(lineItems: any): number {
//   if (!Array.isArray(lineItems)) return 0;

//   return lineItems.reduce((sum, item) => {
//     const amount = Number(item?.Line_Amount || 0);
//     return sum + (isNaN(amount) ? 0 : amount);
//   }, 0);
// }

export function sumLineAmounts(lineItems: any[]): number {
  if (!Array.isArray(lineItems)) return 0;
  const total = lineItems.reduce((sum, item) => {
    console.log((item))
    const amount = toNum(item?.Line_Amount);
    return sum + amount;
  }, 0);
  return Number(total.toFixed(2));
}



export const getFiUser = (processName: any, level: any) => {
  if (processName === "domestic") {
    return level === 'level-2'
  }
  else if (processName === "marcan") {
    return level === 'level-2'
  }
  else if (processName === "tillomed") {
    return level === 'level-2'
  }
  else if (processName === "emerging") {
    return level === 'level-2'
  }
  else {
    return 1
  }
}

export const extractPercentageFromLabel = (label: string): number => {
  if (!label || typeof label !== "string" || label.trim() === "") return 0;

  const text = label.toUpperCase().trim();

  // === 1. Look for number + % (most reliable) ===
  const withPercent = text.match(/(\d+(?:\.\d+)?)\s*(?:%|PCT|PERCENT|PER\s*CENT)/);
  if (withPercent) {
    const num = parseFloat(withPercent[1]);
    if (!isNaN(num) && num >= 0 && num <= 500) return num;
  }

  const numberAtEnd = text.match(/.*?(\d+(?:\.\d+)?)\s*$/);
  if (numberAtEnd) {
    const num = parseFloat(numberAtEnd[1]);
    if (!isNaN(num) && num >= 0 && num <= 500) {
      return num;
    }
  }

  const zeroKeywords = [
    "ZERO", "NIL", "EXEMPT", "EXEMPTED", "NONE",
    "NO VAT", "NOVAT", "NO TAX", "NOTAX",
    "NON-TAXABLE", "NONTAXABLE",
    "EXEMPT SUPPLY", "ZERO RATED", "ZERORATED", "NIL TAX", "NULL"
  ];
  if (zeroKeywords.some(kw => text.includes(kw))) return 0;
  return 0;
};

/**
 * Rounds a number to two decimal places safely.
 */
export const customDecimalRoundSafe = (value: number): number => {
  return Math.round((value + Number.EPSILON) * 100) / 100;
};

/**
 * Calculates total VAT/Tax amount from an array of percentage codes.
 * Each percentage is applied to the net amount and rounded individually.
 */
export const calculateVatAmountFromArray = (netAmount: number | string, taxCodesArr: any[]): number => {
  const netAmountValue = typeof netAmount === "number" ? netAmount : parseFloat(String(netAmount || 0));
  if (isNaN(netAmountValue) || !Array.isArray(taxCodesArr)) return 0;

  let totalTax = 0;
  taxCodesArr.forEach((percent: any) => {
    if (percent !== "" && percent !== null && percent !== undefined) {
      const p = parseFloat(percent.toString());
      if (!isNaN(p)) {
        const taxAmount = customDecimalRoundSafe((netAmountValue * p) / 100);
        totalTax += taxAmount;
      }
    }
  });

  return customDecimalRoundSafe(totalTax);
};